import React, { useState } from 'react';
import { View, Text, TextInput } from 'react-native';
import SectionCard from '../SectionCard';
import { BalancedFeedbackData } from '../../types/beginActivity.common.types';
import { BalancedFeedbackProps } from './BalancedFeedback.types';
import styles from './BalancedFeedback.styles';

// ─── Lane config ──────────────────────────────────────────────────────────────
interface LaneConfig {
  field: keyof BalancedFeedbackData;
  pillTag: string;
  title: string;
  hint: string;
  placeholder: string;
  laneStyle: object;
  headerStyle: object;
  pillStyle: object;
  pillTextStyle: object;
  ariaLabel: string;
}

const LANES: LaneConfig[] = [
  {
    field: 'good',
    pillTag: 'Recognize',
    title: 'What Went Well',
    hint: 'Specific, observable, and worth repeating',
    placeholder: 'What did the associate do well, specifically?',
    laneStyle: styles.laneGood,
    headerStyle: styles.laneHeaderGood,
    pillStyle: styles.pillTagGood,
    pillTextStyle: styles.pillTagTextGood,
    ariaLabel: 'What went well',
  },
  {
    field: 'improve',
    pillTag: 'Coach',
    title: 'Opportunity to Improve',
    hint: 'One thing to work on, and what better looks like',
    placeholder: 'What is the one thing to work on, and what does better look like?',
    laneStyle: styles.laneImprove,
    headerStyle: styles.laneHeaderImprove,
    pillStyle: styles.pillTagImprove,
    pillTextStyle: styles.pillTagTextImprove,
    ariaLabel: 'Opportunity to improve',
  },
  {
    field: 'keep',
    pillTag: 'Sustain',
    title: 'What to Keep Doing',
    hint: 'The habits worth carrying into the next window',
    placeholder: "Which of today's habits should stay exactly as they are?",
    laneStyle: styles.laneKeep,
    headerStyle: styles.laneHeaderKeep,
    pillStyle: styles.pillTagKeep,
    pillTextStyle: styles.pillTagTextKeep,
    ariaLabel: 'What to keep doing',
  },
];

// ─── Single lane ──────────────────────────────────────────────────────────────
function FeedbackLane({
  config,
  value,
  onChange,
  isReadOnly,
}: {
  config: LaneConfig;
  value: string;
  onChange: (val: string) => void;
  isReadOnly: boolean;
}) {
  const [focused, setFocused] = useState(false);

  return (
    <View style={[styles.lane, config.laneStyle]}>
      {/* Coloured header block */}
      <View style={[styles.laneHeader, config.headerStyle]}>
        <View style={[styles.pillTag, config.pillStyle]}>
          <Text style={[styles.pillTagText, config.pillTextStyle]}>
            {config.pillTag}
          </Text>
        </View>
        <Text style={styles.laneTitle}>{config.title}</Text>
        <Text style={styles.laneHint}>{config.hint}</Text>
      </View>

      {/* Input / display */}
      {isReadOnly ? (
        <Text style={[styles.readOnlyText, !value && styles.readOnlyEmpty]}>
          {value || 'Not recorded'}
        </Text>
      ) : (
        <TextInput
          style={[styles.textArea, focused && styles.textAreaFocused]}
          value={value}
          onChangeText={onChange}
          placeholder={config.placeholder}
          placeholderTextColor="#B1B1B1"
          multiline
          textAlignVertical="top"
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          accessibilityLabel={config.ariaLabel}
        />
      )}
    </View>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
const BalancedFeedback: React.FC<BalancedFeedbackProps> = ({
  feedback,
  onChange,
  isReadOnly = false,
}) => {
  const handleChange = (field: keyof BalancedFeedbackData) => (val: string) => {
    onChange({ ...feedback, [field]: val });
  };

  return (
    <SectionCard title="Balanced Feedback" subtitle="Recognize, Coach, Sustain">
      {/* Three-lane grid */}
      <View style={styles.grid}>
        {LANES.map((lane) => (
          <FeedbackLane
            key={lane.field}
            config={lane}
            value={feedback[lane.field]}
            onChange={handleChange(lane.field)}
            isReadOnly={isReadOnly}
          />
        ))}
      </View>
    </SectionCard>
  );
};

export default BalancedFeedback;
